import { FC, useState } from 'react';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Icon } from '@iconify/react';
import {
  Badge,
  Box,
  Chip,
  darken,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Accuracy, CriteriaData, Field } from '@models/CriteriaData';
import { useRouter } from 'next/router';
import ScrollContainer from 'react-indiana-drag-scroll';
import CloseIcon from '@mui/icons-material/Close';
import { INDIGO_ACCENT, INDIGO_PRIMARY, INDIGO_TEXT } from '../../../../colors';
import {
  CriteriaSelector,
  CriteriaSelectorProps,
} from '../../input/AdvancedSearch/CriteriaSelector';
import { LogicalOperator } from '../../input/AdvancedSearch/CriteriaSelector/OperatorMenu';
import { Error } from '../../../elements/Error';
import { mapFieldToLabel } from '../../input/AdvancedSearch/CriteriaSelector/FieldsMenu';

export interface MultiSearchFilterProps {
  title: string;
  pathname: string;
  field: Field;
  required?: boolean;
  criteria?: CriteriaData[];
  criteriaSelectorProps?: CriteriaSelectorProps;
}

export const MultiSearchFilter: FC<MultiSearchFilterProps> = ({
  title,
  pathname,
  field,
  required,
  criteria,
  criteriaSelectorProps,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<string>('');
  const [currentOperator, setCurrentOperator] = useState<LogicalOperator>('OR');
  const [currentAccuracy, setCurrentAccuracy] = useState<Accuracy>('Vague');

  const handleClose = () => {
    setOpen(false);
  };

  const router = useRouter();

  const filter = () => {
    router.push({
      pathname,
      query: {
        ...router.query,
        criteria: JSON.stringify([
          ...criteria,
          {
            q: currentValue,
            field,
            accuracy: currentAccuracy,
            operator: currentOperator,
          },
        ]),
      },
    });

    setCurrentValue('');
  };

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();

      filter();
    }
  }

  const filteredAffiliation = criteria.filter((c) => c.field === field);
  const isRequired = Boolean(
    (!filteredAffiliation || filteredAffiliation?.length === 0) && required,
  );

  const isSelected = filteredAffiliation?.length > 0;

  const [error, setError] = useState<boolean>(false);

  const getBorderCss = () =>
    isRequired
      ? {
          borderColor: 'red',
          borderStyle: 'solid',
          borderWidth: 1,
          borderRadius: 1,
        }
      : {};

  return (
    <Box sx={{ mr: filteredAffiliation?.length > 0 ? 1 : 0 }}>
      <Badge badgeContent={filteredAffiliation?.length} color={'primary'}>
        <Button
          sx={{
            textTransform: 'none',
            fontWeight: isSelected ? 'bold' : 'normal',
            backgroundColor: isSelected ? INDIGO_PRIMARY : 'transparent',
            ...getBorderCss(),
            color: isSelected ? 'white' : INDIGO_TEXT,
            '&:hover': {
              backgroundColor: isSelected
                ? darken(INDIGO_PRIMARY, 0.15)
                : 'rgba(92, 107, 192, 0.08)',
            },
          }}
          onClick={() => setOpen(true)}
          endIcon={
            isSelected ? (
              <Stack
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  router.push({
                    pathname,
                    query: {
                      ...router.query,
                      criteria: JSON.stringify(
                        criteria.filter((c) => !(c.field === field)),
                      ),
                    },
                  });
                }}
              >
                <CloseIcon
                  sx={{ fontSize: 20, '&:hover': { color: INDIGO_TEXT } }}
                />
              </Stack>
            ) : null
          }
        >
          <>
            <span>
              <span style={{ textDecoration: isSelected ? 'underline' : null }}>
                {!isSelected ? title : `${mapFieldToLabel(field)}`}
              </span>
              {isSelected && <span>{`: ${filteredAffiliation[0]?.q}`}</span>}
            </span>
            {!isSelected ? (
              <ArrowDropDownIcon sx={{ color: isRequired ? 'red' : null }} />
            ) : null}
          </>
        </Button>
      </Badge>
      <Dialog onClose={handleClose} open={open} maxWidth={'md'}>
        <Paper elevation={2} sx={{ p: 2, width: 'fit-content' }}>
          <ScrollContainer horizontal style={{ display: 'flex' }}>
            <Stack
              direction={'row'}
              spacing={1}
              sx={{ mb: isSelected ? 2 : 0 }}
              justifyContent={'center'}
            >
              {filteredAffiliation.map((criteriaData) => (
                <Chip
                  key={`selected-${criteriaData.q}-typography`}
                  label={criteriaData.q}
                  onDelete={() => {
                    router.push({
                      pathname,
                      query: {
                        ...router.query,
                        criteria: JSON.stringify(
                          criteria.filter(
                            (c) =>
                              !(
                                c.q === criteriaData.q &&
                                c.field === field &&
                                c.operator === criteriaData.operator &&
                                c.accuracy === criteriaData.accuracy
                              ),
                          ),
                        ),
                      },
                    });
                  }}
                />
              ))}
            </Stack>
          </ScrollContainer>
          <Stack spacing={1}>
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
              <Icon icon="el:filter" color={'gray'} />
              <Typography>{`${mapFieldToLabel(field)} Filter`}</Typography>
            </Stack>
            {error && (
              <Error
                error={Boolean(error)}
                onCloseCallback={() => {
                  setError(null);
                }}
              >
                {'Please fill in search criteria...'}
              </Error>
            )}
            <Stack direction={'row'} alignItems={'center'}>
              <CriteriaSelector
                {...criteriaSelectorProps}
                withStartEmptySpace={false}
                autoFocus={true}
                error={error}
                inputWidth={170}
                onQueryChange={(q) => {
                  setError(false);
                  setCurrentValue(q);
                }}
                onOperatorSelected={(logicalOperator: LogicalOperator) => {
                  setCurrentOperator(logicalOperator);
                }}
                onAccuracySelected={(accuracy: Accuracy) => {
                  setCurrentAccuracy(accuracy);
                }}
                handleKeyDown={handleKeyDown}
                onFieldSelected={(field: Field) => {}}
                onFieldOptionSelected={(q) => setCurrentValue(q)}
                withLogicalOperator={isSelected}
                options={{
                  fields: [field],
                }}
                value={currentValue}
              />
              <IconButton
                sx={{ height: 50, width: 50 }}
                onClick={() => {
                  if (currentValue !== '') {
                    filter();
                  } else {
                    setError(true);
                  }
                }}
              >
                <Icon
                  icon={'bi:filter-circle-fill'}
                  color={currentValue !== '' ? INDIGO_ACCENT : 'gray'}
                />
              </IconButton>
            </Stack>
          </Stack>
        </Paper>
      </Dialog>
    </Box>
  );
};
